import { RefCallback, useCallback, useId, useMemo, useRef, useSyncExternalStore } from 'react';

import { ColumnEdge, EPSILON_PX, ScrollDirection, scrollPageTarget } from '../lib/scroll-page';

export interface HorizontalScroll {
  /** Callback ref for the scroll container */
  ref: RefCallback<HTMLDivElement>;
  /** id for the scroll container, so the arrows can name it in aria-controls */
  id: string;
  /** The edge flags as an external store — read them with `useScrollEdges` */
  subscribe: (onChange: () => void) => () => void;
  getEdges: () => ScrollEdges;
  scrollPage: (direction: ScrollDirection) => void;
}

export interface ScrollEdges {
  canScrollStart: boolean;
  canScrollEnd: boolean;
}

const NO_OVERFLOW: ScrollEdges = { canScrollStart: false, canScrollEnd: false };

/** Interactions that take a running step over — its target may then never be reached */
const TAKEOVER_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * Horizontal scroll state of a data table's scroll container, for the scroll arrows.
 *
 * - Marks which edges currently hide content (`data-scroll-start` / `data-scroll-end`), so pinned cells
 *   show their edge separator only while something actually slides underneath them — a table that fits
 *   stays shadow-free (MUI-like). The same flags offer the grab cursor of `useDragScroll`.
 * - Mirrors the flags for the arrows as an external store, not as state: the owner is DataTable, and a state
 *   change there re-renders every row each time the table leaves or reaches an edge (85–250 ms frames in dev).
 *   Only the components that read the flags through `useScrollEdges` re-render, and only when a flag flips.
 * - `scrollPage` steps a page on column boundaries (`scrollPageTarget`).
 *
 * The container attaches through the callback ref, so one that mounts later (the fullscreen dialog's)
 * is wired when it appears.
 *
 * The ResizeObserver is attached once to the container's first child (the table wrapper), relying on
 * React keeping that DOM node stable across re-renders — don't render children whose first element
 * changes identity.
 */
export function useHorizontalScroll(): HorizontalScroll {
  const id = useId();
  const elementRef = useRef<HTMLDivElement | null>(null);
  /**
   * Where a step still animating will land: a quick second press continues from here, not from
   * mid-animation. Cleared once scrollLeft gets there, or by anything that takes the scroll over.
   * Neither `scrollend` (missing in Safari) nor a timer (smooth-scroll duration grows with distance).
   */
  const pendingTarget = useRef<number | null>(null);
  const edges = useRef(NO_OVERFLOW);
  const listeners = useRef(new Set<() => void>());

  const setEdges = useCallback((next: ScrollEdges) => {
    const { canScrollStart, canScrollEnd } = edges.current;
    if (next.canScrollStart === canScrollStart && next.canScrollEnd === canScrollEnd) return;
    edges.current = next;
    listeners.current.forEach((onChange) => onChange());
  }, []);
  const subscribe = useCallback((onChange: () => void) => {
    listeners.current.add(onChange);
    return () => {
      listeners.current.delete(onChange);
    };
  }, []);
  const getEdges = useCallback(() => edges.current, []);

  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return;
      elementRef.current = el;

      const update = () => {
        const start = el.scrollLeft > 0;
        const end = el.scrollLeft + el.clientWidth < el.scrollWidth - EPSILON_PX;
        el.toggleAttribute('data-scroll-start', start);
        el.toggleAttribute('data-scroll-end', end);
        setEdges({ canScrollStart: start, canScrollEnd: end });
        if (pendingTarget.current !== null && Math.abs(el.scrollLeft - pendingTarget.current) <= EPSILON_PX) {
          pendingTarget.current = null;
        }
      };
      const dropTarget = () => {
        pendingTarget.current = null;
      };
      // New bounds may leave the pending target unreachable
      const onResize = () => {
        dropTarget();
        update();
      };

      update();
      el.addEventListener('scroll', update, { passive: true });
      TAKEOVER_EVENTS.forEach((type) => el.addEventListener(type, dropTarget, { passive: true }));
      const resizeObserver = new ResizeObserver(onResize);
      resizeObserver.observe(el);
      // content width changes too (columns toggled, data loaded)
      if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);

      return () => {
        el.removeEventListener('scroll', update);
        TAKEOVER_EVENTS.forEach((type) => el.removeEventListener(type, dropTarget));
        resizeObserver.disconnect();
        elementRef.current = null;
        pendingTarget.current = null;
        setEdges(NO_OVERFLOW);
      };
    },
    [setEdges]
  );

  const scrollPage = useCallback((direction: ScrollDirection) => {
    const el = elementRef.current;
    if (!el) return;

    const target = scrollPageTarget(
      {
        columns: measureColumns(el),
        scrollLeft: pendingTarget.current ?? el.scrollLeft,
        clientWidth: el.clientWidth,
        scrollWidth: el.scrollWidth
      },
      direction
    );
    // Already there: no scroll event would come to clear it
    pendingTarget.current = Math.abs(target - el.scrollLeft) <= EPSILON_PX ? null : target;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, []);

  // Stable for the table's lifetime: handing it down re-renders nothing
  return useMemo(() => ({ ref, id, subscribe, getEdges, scrollPage }), [ref, id, subscribe, getEdges, scrollPage]);
}

const subscribeToNothing = () => () => {};
const getNoOverflow = () => NO_OVERFLOW;

/** The edge flags of a `useHorizontalScroll` container; re-renders the caller only when a flag flips */
export function useScrollEdges(scroll: Pick<HorizontalScroll, 'subscribe' | 'getEdges'> | undefined): ScrollEdges {
  return useSyncExternalStore(scroll?.subscribe ?? subscribeToNothing, scroll?.getEdges ?? getNoOverflow);
}

/**
 * Header cells of the table — the data table has exactly one header row, its pinned cells marked `data-pinned`.
 * A sticky cell's rect is where it is drawn, not where it sits in the content: for pinned cells only the width
 * is meaningful, and that is all `scrollPageTarget` takes from them.
 */
function measureColumns(el: HTMLElement): ColumnEdge[] {
  const box = el.getBoundingClientRect();
  return Array.from(el.querySelector('thead')?.rows[0]?.cells ?? [], (cell) => {
    const rect = cell.getBoundingClientRect();
    return {
      left: rect.left - box.left + el.scrollLeft,
      right: rect.right - box.left + el.scrollLeft,
      pinned: cell.dataset.pinned as ColumnEdge['pinned']
    };
  });
}
